import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Download, MapPin, Navigation, Search } from 'lucide-react';
import EventMap, {
  Coordinates,
  EventMarkerData,
  toEventShape,
} from '../components/EventMap';
import MapEventList from '../components/MapEventList';
import EventFilterBar from '../components/events/EventFilterBar';
import MapSelectionPane from '../components/events/MapSelectionPane';
import SEO, { createBreadcrumbStructuredData } from '../components/SEO';
import EventFinderAPI from '../services/api';
import type { Event, EventGroupMapLocation } from '../types/api';
import { useAnalytics } from '../hooks/useAnalytics';
import {
  countActiveFilters,
  parseFiltersFromParams,
  writeFiltersToParams,
  type EventFilterState,
} from '../utils/eventFilters';
import { isUpcomingOrCurrent } from '../utils/eventPresentation';
import type { ExportFormat } from '../utils/eventExport';
import { borderRadius, breakpoints, colors, shadows, spacing, typography } from '../styles/theme';

const DEFAULT_RADIUS_KM = 15;
const RADIUS_OPTIONS = [5, 15, 25, 50, 100, 250] as const;
const FILTER_KEYS = ['days', 'from', 'to', 'format', 'rel', 'tod', 'vibe', 'hideEntity'];
const FALLBACK_CENTER: Coordinates = { lat: 39.8283, lng: -98.5795 };

interface NearbyRouteState {
  eventsNearMeCenter?: Coordinates;
  radiusKm?: number;
}

const Page = styled.main`
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const Container = styled.div`
  width: min(1440px, 100%);
  margin: 0 auto;
  padding: ${spacing[6]} ${spacing[4]} ${spacing[12]};

  @media (min-width: ${breakpoints.md}) {
    padding: ${spacing[8]} ${spacing[6]} ${spacing[12]};
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing[6]};
  margin-bottom: ${spacing[5]};

  @media (max-width: ${breakpoints.md}) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${spacing[4]};
  }
`;

const HeaderCopy = styled.div`
  max-width: 760px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
`;

const Title = styled.h1`
  margin: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes['3xl']};
  line-height: ${typography.lineHeights.tight};

  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const Description = styled.p`
  margin: ${spacing[2]} 0 0;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.base};
  line-height: ${typography.lineHeights.relaxed};
`;

const ExportGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  min-height: 44px;
  padding: 0 ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${colors.primary[400]};
    color: ${colors.primary[700]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Toolbar = styled.section`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  box-shadow: ${shadows.sm};

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const RadiusField = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};

  select {
    min-width: 94px;
    min-height: 44px;
    padding: 0 ${spacing[8]} 0 ${spacing[3]};
    border: 1px solid ${colors.border.normal};
    border-radius: ${borderRadius.md};
    background: ${colors.white};
    color: ${colors.text.primary};
    font: inherit;

    &:focus-visible {
      outline: 2px solid ${colors.border.focus};
      outline-offset: 2px;
    }
  }
`;

const FilterSummary = styled.span`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const Notice = styled.div`
  margin-bottom: ${spacing[4]};
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.warning[100]};
  border-radius: ${borderRadius.md};
  background: ${colors.warning[50]};
  color: ${colors.warning[700]};
  font-size: ${typography.fontSizes.sm};
`;

const ErrorNotice = styled(Notice)`
  border-color: ${colors.border.normal};
  background: ${colors.background.secondary};
  color: ${colors.text.primary};
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[5]};
  margin-bottom: ${spacing[3]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};

  strong {
    color: ${colors.text.primary};
  }
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const SkeletonStat = styled.span`
  width: 88px;
  height: 16px;
  border-radius: ${borderRadius.sm};
  background: linear-gradient(
    90deg,
    ${colors.gray[100]} 25%,
    ${colors.gray[200]} 50%,
    ${colors.gray[100]} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;

  &:nth-child(2) {
    width: 106px;
  }

  &:nth-child(3) {
    width: 76px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MapLayout = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
  gap: ${spacing[4]};
  height: 640px;

  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(420px, 58vh) 400px;
    height: auto;
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-rows: minmax(360px, 54vh) 380px;
  }
`;

const MapPane = styled.div`
  position: relative;
  min-width: 0;
  min-height: 0;
`;

const MapLoadingOverlay = styled.div`
  position: absolute;
  z-index: 1050;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  background: ${colors.background.secondary};

  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background: linear-gradient(
      100deg,
      transparent 20%,
      ${colors.gray[200]} 50%,
      transparent 80%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const MapLoadingMessage = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  box-shadow: ${shadows.sm};
`;

const ListPane = styled.div`
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const MapOverlay = styled.div`
  position: absolute;
  z-index: 1100;
  left: 50%;
  bottom: ${spacing[4]};
  transform: translateX(-50%);
`;

const SearchAreaButton = styled(Button)`
  border-color: ${colors.primary[200]};
  border-radius: ${borderRadius.full};
  color: ${colors.primary[700]};
  box-shadow: ${shadows.md};
  white-space: nowrap;
`;

const LoadingPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 440px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
  color: ${colors.text.secondary};
`;

const LocationLoadingSkeleton = styled(LoadingPanel)`
  position: relative;
  flex-direction: column;
  gap: ${spacing[3]};
  overflow: hidden;

  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background: linear-gradient(
      100deg,
      transparent 20%,
      ${colors.gray[200]} 50%,
      transparent 80%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s ease-in-out infinite;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const LocationPrompt = styled(LoadingPanel)`
  flex-direction: column;
  gap: ${spacing[3]};
  text-align: center;
  padding: ${spacing[6]};
`;

function radiusFromParams(params: URLSearchParams): number {
  const parsed = Number(params.get('radiusKm'));
  return RADIUS_OPTIONS.includes(parsed as (typeof RADIUS_OPTIONS)[number])
    ? parsed
    : DEFAULT_RADIUS_KM;
}

function filterParams(params: URLSearchParams): URLSearchParams {
  const filters = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value) filters.set(key, value);
  }
  return filters;
}

function mapLocationEvents(location: EventGroupMapLocation): EventMarkerData[] {
  return location.events.map(event => ({
    ...event,
    city: location.city || undefined,
    state: location.state || undefined,
    country: location.country || undefined,
    coordinates: {
      lat: location.coordinates.latitude,
      lng: location.coordinates.longitude,
    },
    coordinatesSource: location.coordinates.source,
    entities: [],
  }));
}

function distanceKm(left: Coordinates, right: Coordinates): number {
  const radius = 6371;
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = radians(right.lat - left.lat);
  const longitudeDelta = radians(right.lng - left.lng);
  const value = Math.sin(latitudeDelta / 2) ** 2
    + Math.sin(longitudeDelta / 2) ** 2
      * Math.cos(radians(left.lat)) * Math.cos(radians(right.lat));
  return 2 * radius * Math.asin(Math.sqrt(value));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const MapPageComponent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const routeState = location.state as NearbyRouteState | null;
  const radiusKm = radiusFromParams(searchParams);
  const filters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);
  const apiFilterKey = filterParams(searchParams).toString();

  const [requestCenter, setRequestCenter] = useState<Coordinates | null>(null);
  const [searchedCenter, setSearchedCenter] = useState<Coordinates | null>(null);
  const [mapCenter, setMapCenter] = useState<Coordinates>(FALLBACK_CENTER);
  const [mapZoom, setMapZoom] = useState(4);
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [events, setEvents] = useState<EventMarkerData[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalLocations, setTotalLocations] = useState(0);
  const [vibeOptions, setVibeOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [highlightedEventId, setHighlightedEventId] = useState<number | null>(null);
  const [focusedEventId, setFocusedEventId] = useState<number | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<EventMarkerData[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geoNotice, setGeoNotice] = useState<string | null>(null);
  const [pannedSinceSearch, setPannedSinceSearch] = useState(false);
  const viewportRef = useRef({ center: FALLBACK_CENTER, zoom: 4 });
  const initializedRef = useRef(false);

  const beginSearch = useCallback((center: Coordinates, zoom = 10) => {
    setMapLoading(true);
    setRequestCenter(center);
    setSearchedCenter(center);
    setMapCenter(center);
    setMapZoom(zoom);
    viewportRef.current = { center, zoom };
    setPannedSinceSearch(false);
  }, []);

  const requestLocation = useCallback(() => {
    setLoading(true);
    setError(null);
    setGeoNotice(null);
    if (!navigator.geolocation) {
      setGeoNotice('Location is not available in this browser.');
      setLoading(false);
      setMapLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        const center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(center);
        beginSearch(center);
      },
      geoError => {
        const reason = geoError.code === geoError.PERMISSION_DENIED
          ? 'Location permission was denied.'
          : 'Your location could not be determined.';
        setGeoNotice(`${reason} You can enable location and try again.`);
        setLoading(false);
        setMapLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 },
    );
  }, [beginSearch]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const routeCenter = routeState?.eventsNearMeCenter;
    if (routeCenter && Number.isFinite(routeCenter.lat) && Number.isFinite(routeCenter.lng)) {
      setUserLocation(routeCenter);
      beginSearch(routeCenter);
      if (routeState?.radiusKm && RADIUS_OPTIONS.includes(routeState.radiusKm as never)) {
        const next = new URLSearchParams(searchParams);
        next.set('radiusKm', String(routeState.radiusKm));
        setSearchParams(next, { replace: true });
      }
      return;
    }
    requestLocation();
  }, [beginSearch, requestLocation, routeState, searchParams, setSearchParams]);

  useEffect(() => {
    if (!requestCenter) return;
    const controller = new AbortController();
    let cancelled = false;
    let mappedEvents: EventMarkerData[] | null = null;
    let details = new Map<number, Event>();
    const params = new URLSearchParams(apiFilterKey);
    setLoading(true);
    setMapLoading(true);
    setError(null);

    const publishEvents = () => {
      if (!mappedEvents) return;
      setEvents(mappedEvents.map(marker => ({
        ...marker,
        ...(details.get(marker.eventId) || {}),
        coordinates: marker.coordinates,
        coordinatesSource: marker.coordinatesSource,
      })));
    };

    const listRequest = EventFinderAPI.getNearbyEvents(
      requestCenter.lat,
      requestCenter.lng,
      radiusKm,
      params,
      1,
      100,
      controller.signal,
    ).then(listResult => {
      if (cancelled) return;
      details = new Map(listResult.events.map(event => [event.eventId, event]));
      setTotalEvents(listResult.pagination.totalItems);
      setVibeOptions(listResult.facets.vibes);
      setEntityOptions(listResult.facets.entities
        .map(facet => ({ value: facet.entityId, label: facet.displayName }))
        .sort((left, right) => left.label.localeCompare(right.label)));
      publishEvents();
    }).catch(loadError => {
      if (cancelled || (loadError as Error).name === 'AbortError') return;
      setError((loadError as Error).message || 'Nearby event details could not be loaded.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    const mapRequest = EventFinderAPI.getNearbyEventMap(
      requestCenter.lat,
      requestCenter.lng,
      radiusKm,
      params,
      controller.signal,
    ).then(mapResult => {
      if (cancelled) return;
      const currentLocations = mapResult.locations
        .map(mapLocation => ({
          ...mapLocation,
          events: mapLocation.events.filter(event => isUpcomingOrCurrent(event)),
        }))
        .filter(mapLocation => mapLocation.events.length > 0);
      mappedEvents = currentLocations.flatMap(mapLocationEvents);
      setTotalEvents(mappedEvents.length);
      setTotalLocations(currentLocations.length);
      publishEvents();
      setSelectedEvents([]);
      setSelectedEventId(null);
    }).catch(loadError => {
      if (cancelled || (loadError as Error).name === 'AbortError') return;
      setError((loadError as Error).message || 'The nearby event map could not be loaded.');
    }).finally(() => {
      if (!cancelled) setMapLoading(false);
    });

    Promise.allSettled([listRequest, mapRequest]).then(() => {
      if (!cancelled) setSearching(false);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiFilterKey, radiusKm, requestCenter]);

  const updateFilters = useCallback((nextFilters: EventFilterState) => {
    const changedGroup = (Object.keys(nextFilters) as Array<keyof EventFilterState>)
      .find(key => JSON.stringify(filters[key]) !== JSON.stringify(nextFilters[key]));
    setSearchParams(writeFiltersToParams(searchParams, nextFilters), { replace: true });
    trackEvent({
      category: 'events_near_me',
      action: 'filter_change',
      custom_parameters: {
        filter_group: changedGroup || 'unknown',
        active_filters: countActiveFilters(nextFilters),
        horizon_days: nextFilters.horizonDays,
      },
    });
  }, [filters, searchParams, setSearchParams, trackEvent]);

  const updateRadius = useCallback((nextRadius: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextRadius === DEFAULT_RADIUS_KM) next.delete('radiusKm');
    else next.set('radiusKm', String(nextRadius));
    setSearchParams(next, { replace: true });
    trackEvent({
      category: 'events_near_me',
      action: 'radius_change',
      value: nextRadius,
      custom_parameters: { radius_km: nextRadius },
    });
  }, [searchParams, setSearchParams, trackEvent]);

  const handleMapMove = useCallback((center: Coordinates, zoom: number) => {
    viewportRef.current = { center, zoom };
    setPannedSinceSearch(true);
  }, []);

  const handleSearchArea = useCallback(() => {
    const { center } = viewportRef.current;
    setSearching(true);
    setMapLoading(true);
    setRequestCenter(center);
    setSearchedCenter(center);
    setPannedSinceSearch(false);
    trackEvent({
      category: 'events_near_me',
      action: 'search_area',
      custom_parameters: { radius_km: radiusKm },
    });
  }, [radiusKm, trackEvent]);

  const handleMarkerSelect = useCallback((markerEvents: EventMarkerData[]) => {
    if (markerEvents.length === 0) return;
    setSelectedEvents(markerEvents);
    setSelectedEventId(markerEvents[0].eventId);
    setHighlightedEventId(markerEvents[0].eventId);
    trackEvent({
      category: 'events_near_me',
      action: 'map_details_open',
      value: markerEvents.length,
    });
  }, [trackEvent]);

  const closeSelection = useCallback(() => {
    setSelectedEvents([]);
    setSelectedEventId(null);
    setHighlightedEventId(null);
  }, []);

  const findInList = useCallback((eventId: number) => {
    setFocusedEventId(eventId);
    setHighlightedEventId(eventId);
    window.requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(`[data-event-id="${eventId}"]`);
      if (!row) return;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      row.focus({ preventScroll: true });
    });
    trackEvent({ category: 'events_near_me', action: 'map_find_in_list' });
  }, [trackEvent]);

  const handleExport = useCallback(async (format: ExportFormat) => {
    if (!requestCenter || totalEvents === 0) return;
    try {
      const blob = await EventFinderAPI.getNearbyEventsExport(
        requestCenter.lat,
        requestCenter.lng,
        radiusKm,
        format,
        new URLSearchParams(apiFilterKey),
      );
      const extension = format === 'markdown' ? 'md' : format === 'text' ? 'txt' : 'csv';
      downloadBlob(blob, `events-near-me.${extension}`);
      trackEvent({
        category: 'events_near_me',
        action: 'export_events',
        label: format,
        value: totalEvents,
        custom_parameters: { format, radius_km: radiusKm },
      });
    } catch (exportError) {
      setError((exportError as Error).message || 'The Events Near Me export could not be created.');
    }
  }, [apiFilterKey, radiusKm, requestCenter, totalEvents, trackEvent]);

  const selectedEventShapes = useMemo(
    () => selectedEvents.map(toEventShape),
    [selectedEvents],
  );
  const uniqueCities = useMemo(
    () => new Set(events.map(event => [event.city, event.state, event.country].filter(Boolean).join('|'))).size,
    [events],
  );
  const showSearchArea = Boolean(
    searchedCenter
      && pannedSinceSearch
      && distanceKm(viewportRef.current.center, searchedCenter) > 5,
  );
  const horizonDescription = filters.dateTo
    ? `through ${filters.dateTo}`
    : `in the next ${filters.horizonDays} days`;

  return (
    <Page>
      <SEO
        title="Events Near Me | Event Finder"
        description="Find upcoming events near your location with shared date, format, relationship, time, and vibe filters."
        keywords="events near me, local events, nearby conferences, nearby workshops, event map"
        canonical="https://www.eventfinder.site/map"
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: 'https://www.eventfinder.site/' },
            { name: 'Events Near Me', url: 'https://www.eventfinder.site/map' },
          ]),
        ]}
      />
      <Container>
        <Header>
          <HeaderCopy>
            <Eyebrow><Navigation aria-hidden="true" size={16} /> Location discovery</Eyebrow>
            <Title>Events Near Me</Title>
            <Description>
              {requestCenter
                ? mapLoading && events.length === 0
                  ? `Finding events within ${radiusKm} km ${horizonDescription}.`
                  : `${totalEvents} event${totalEvents === 1 ? '' : 's'} within ${radiusKm} km ${horizonDescription}.`
                : `Use your location to find upcoming events ${horizonDescription}.`}
            </Description>
          </HeaderCopy>
          <ExportGroup role="group" aria-label="Export events">
            {(['csv', 'markdown', 'text'] as ExportFormat[]).map(format => (
              <Button
                key={format}
                type="button"
                onClick={() => handleExport(format)}
                disabled={!requestCenter || totalEvents === 0}
                aria-label={`Export ${format === 'csv' ? 'CSV' : format === 'markdown' ? 'Markdown' : 'text'}`}
              >
                <Download aria-hidden="true" size={16} />
                {format === 'csv' ? 'CSV' : format === 'markdown' ? 'Markdown' : 'Text'}
              </Button>
            ))}
          </ExportGroup>
        </Header>

        <Toolbar aria-label="Events Near Me controls">
          <EventFilterBar
            filters={filters}
            onChange={updateFilters}
            vibeOptions={vibeOptions}
            entityOptions={entityOptions}
            resultSummary={(
              <FilterSummary>
                {loading ? 'Loading events...' : `${totalEvents} result${totalEvents === 1 ? '' : 's'}`}
              </FilterSummary>
            )}
            isLoading={loading}
            resultCount={totalEvents}
          />
          <RadiusField>
            <MapPin aria-hidden="true" size={16} />
            Radius
            <select
              aria-label="Search radius"
              value={radiusKm}
              onChange={event => updateRadius(Number(event.target.value))}
            >
              {RADIUS_OPTIONS.map(option => (
                <option key={option} value={option}>{`${option} km`}</option>
              ))}
            </select>
          </RadiusField>
        </Toolbar>

        {geoNotice && <Notice role="status">{geoNotice}</Notice>}
        {error && <ErrorNotice role="alert">{error}</ErrorNotice>}

        {!requestCenter && loading && (
          <LocationLoadingSkeleton role="status" aria-label="Requesting your location">
            <MapPin aria-hidden="true" size={28} />
            <span>Requesting your location...</span>
          </LocationLoadingSkeleton>
        )}
        {!requestCenter && !loading && (
          <LocationPrompt>
            <MapPin aria-hidden="true" size={32} />
            <strong>Location is needed to search nearby.</strong>
            <Button type="button" onClick={requestLocation}>Use my location</Button>
          </LocationPrompt>
        )}

        {requestCenter && (
          <>
            <Stats aria-live="polite" aria-label={mapLoading && events.length === 0 ? 'Loading nearby event totals' : undefined}>
              {mapLoading && events.length === 0 ? (
                <>
                  <SkeletonStat aria-hidden="true" />
                  <SkeletonStat aria-hidden="true" />
                  <SkeletonStat aria-hidden="true" />
                </>
              ) : (
                <>
                  <span><strong>{totalEvents}</strong> events</span>
                  <span><strong>{totalLocations}</strong> locations</span>
                  <span><strong>{uniqueCities}</strong> cities</span>
                </>
              )}
            </Stats>
            <MapLayout aria-busy={loading || mapLoading}>
              <MapPane>
                <EventMap
                  events={events}
                  center={mapCenter}
                  zoom={mapZoom}
                  userLocation={userLocation || undefined}
                  onMapMove={handleMapMove}
                  highlightedEventId={highlightedEventId}
                  focusedEventId={focusedEventId}
                  onMarkerHover={setHighlightedEventId}
                  onMarkerSelect={handleMarkerSelect}
                  height="100%"
                />
                {mapLoading && (
                  <MapLoadingOverlay
                    role="status"
                    aria-live="polite"
                    aria-label={events.length === 0 ? 'Loading nearby events' : 'Updating nearby events'}
                  >
                    <MapLoadingMessage>
                      <MapPin aria-hidden="true" size={16} />
                      {events.length === 0 ? 'Finding events nearby...' : 'Updating this area...'}
                    </MapLoadingMessage>
                  </MapLoadingOverlay>
                )}
                {showSearchArea && (
                  <MapOverlay>
                    <SearchAreaButton type="button" onClick={handleSearchArea} disabled={searching}>
                      <Search aria-hidden="true" size={16} />
                      {searching ? 'Searching...' : 'Search this area'}
                    </SearchAreaButton>
                  </MapOverlay>
                )}
                {selectedEventId != null && selectedEventShapes.length > 0 && (
                  <MapSelectionPane
                    events={selectedEventShapes}
                    activeEventId={selectedEventId}
                    onSelectEvent={eventId => {
                      setSelectedEventId(eventId);
                      setHighlightedEventId(eventId);
                    }}
                    onClose={closeSelection}
                    onFindInList={findInList}
                    onViewEvent={() => trackEvent({
                      category: 'events_near_me',
                      action: 'map_view_event',
                    })}
                  />
                )}
              </MapPane>
              <ListPane>
                <MapEventList
                  events={events}
                  isLoading={mapLoading && events.length === 0}
                  highlightedEventId={highlightedEventId}
                  onHighlightChange={eventId => {
                    setHighlightedEventId(eventId);
                    setFocusedEventId(eventId);
                  }}
                />
              </ListPane>
            </MapLayout>
          </>
        )}
      </Container>
    </Page>
  );
};

export default MapPageComponent;